/**
 * Isolated sample payload for UI development and when the live API returns 501.
 * Do not import this from production API handlers.
 */
import type { DripDashboardSnapshot } from './types';

function heatmap(): DripDashboardSnapshot['heatmap'] {
  const cells: DripDashboardSnapshot['heatmap'] = [];
  for (let d = 0; d < 7; d += 1) {
    for (let h = 9; h < 21; h += 1) {
      cells.push({
        dayIndex: d,
        hour: h,
        value: ((d * 17 + h * 11) % 55) + 30,
      });
    }
  }
  return cells;
}

export const DRIP_DASHBOARD_FIXTURE: DripDashboardSnapshot = {
  kpis: [
    { id: 'k1', label: 'Total campaigns', value: '38', deltaPct: 4.2, trend: 'up', icon: 'campaigns', hint: 'All time' },
    { id: 'k2', label: 'Active', value: '12', deltaPct: 1.0, trend: 'up', icon: 'active', hint: 'Sending now' },
    { id: 'k3', label: 'Emails sent', value: '284K', deltaPct: 6.8, trend: 'up', icon: 'sent', hint: 'Trailing 30d' },
    { id: 'k4', label: 'Open rate', value: '41.2%', deltaPct: -0.8, trend: 'down', icon: 'open', hint: 'Unique opens / delivered' },
    { id: 'k5', label: 'Click rate', value: '9.4%', deltaPct: 1.1, trend: 'up', icon: 'click', hint: 'Unique clicks / delivered' },
    { id: 'k6', label: 'Conversion rate', value: '2.7%', deltaPct: 0.3, trend: 'up', icon: 'convert', hint: 'Attributed conversions' },
    { id: 'k7', label: 'Revenue', value: '$1.04M', deltaPct: 3.4, trend: 'up', icon: 'revenue', hint: 'Attributed pipeline' },
    { id: 'k8', label: 'Unsubscribes', value: '0.32%', deltaPct: -12.0, trend: 'up', icon: 'unsub', hint: 'Lower is better' },
  ],
  series: Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    return {
      date: d.toISOString().slice(0, 10),
      opens: 12000 + i * 420 + (i % 3) * 800,
      clicks: 2100 + i * 90 + (i % 4) * 120,
      conversions: 180 + i * 12 + (i % 2) * 25,
    };
  }),
  funnel: [
    { key: 'sent', label: 'Sent', count: 284000 },
    { key: 'opened', label: 'Opened', count: 117000 },
    { key: 'clicked', label: 'Clicked', count: 26700 },
    { key: 'converted', label: 'Converted', count: 7680 },
  ],
  heatmap: heatmap(),
  devices: [
    { device: 'Mobile', pct: 58 },
    { device: 'Desktop', pct: 35 },
    { device: 'Tablet', pct: 7 },
  ],
  topCampaigns: [
    { name: 'Accredited nurture v3', engagementScore: 94 },
    { name: 'Event follow-up — Austin', engagementScore: 88 },
    { name: 'IRA education — module 2', engagementScore: 81 },
    { name: 'Deal teaser — Tampa MF', engagementScore: 76 },
    { name: 'Re-engagement — cold Q1', engagementScore: 71 },
  ],
  campaigns: [
    {
      id: 'c1',
      name: 'Accredited investor nurture',
      status: 'active',
      audience: 'Accredited · US',
      currentStep: 'Email 3 / Wait 48h',
      sent: 45200,
      opens: 19200,
      ctrPct: 10.2,
      replies: 320,
      conversions: 412,
      revenueUsd: 240000,
      updatedAt: new Date(Date.now() - 3600e3).toISOString(),
    },
    {
      id: 'c2',
      name: 'Event — walking tour follow-up',
      status: 'active',
      audience: 'Event RSVPs',
      currentStep: 'SMS reminder',
      sent: 8900,
      opens: 4100,
      ctrPct: 14.1,
      replies: 89,
      conversions: 56,
      revenueUsd: 18500,
      updatedAt: new Date(Date.now() - 7200e3).toISOString(),
    },
    {
      id: 'c3',
      name: 'IRA self-direct workshop',
      status: 'paused',
      audience: 'IRA interest leads',
      currentStep: 'Paused at condition',
      sent: 22100,
      opens: 9800,
      ctrPct: 8.4,
      replies: 156,
      conversions: 201,
      revenueUsd: 62000,
      updatedAt: new Date(Date.now() - 86400e3).toISOString(),
    },
    {
      id: 'c4',
      name: 'Deal allocation — Series B',
      status: 'draft',
      audience: 'Qualified LPs',
      currentStep: 'Draft — review',
      sent: 0,
      opens: 0,
      ctrPct: 0,
      replies: 0,
      conversions: 0,
      revenueUsd: 0,
      updatedAt: new Date(Date.now() - 1209600e3).toISOString(),
    },
    {
      id: 'c5',
      name: 'Win-back Q2',
      status: 'completed',
      audience: 'Dormant 90d',
      currentStep: 'Completed',
      sent: 12400,
      opens: 5100,
      ctrPct: 6.2,
      replies: 42,
      conversions: 98,
      revenueUsd: 12000,
      updatedAt: new Date(Date.now() - 1814400e3).toISOString(),
    },
    {
      id: 'c6',
      name: 'Webinar replay sequence',
      status: 'active',
      audience: 'Webinar attendees',
      currentStep: 'Email 2',
      sent: 6700,
      opens: 3100,
      ctrPct: 11.0,
      replies: 55,
      conversions: 72,
      revenueUsd: 28000,
      updatedAt: new Date(Date.now() - 1800e3).toISOString(),
    },
  ],
  workflow: [
    { id: 'w1', kind: 'trigger', title: 'Trigger', subtitle: 'Lead submits IRA form' },
    { id: 'w2', kind: 'email', title: 'Email', subtitle: 'Welcome + custodian checklist' },
    { id: 'w3', kind: 'wait', title: 'Wait', subtitle: '48 hours' },
    { id: 'w4', kind: 'condition', title: 'Condition', subtitle: 'Opened previous email?' },
    { id: 'w5', kind: 'branch', title: 'Branch', subtitle: 'Yes / No paths' },
    { id: 'w6', kind: 'sms', title: 'SMS', subtitle: 'Reminder — book call' },
    { id: 'w7', kind: 'webhook', title: 'Webhook', subtitle: 'POST /crm/events' },
    { id: 'w8', kind: 'goal', title: 'Goal', subtitle: 'Booked meeting' },
  ],
  segmentRules: [
    { id: 's1', label: 'Accredited = true', active: true },
    { id: 's2', label: 'Lead score ≥ 60', active: true },
    { id: 's3', label: 'Tag: event_rsvp', active: false },
    { id: 's4', label: 'Behavior: clicked deal link (30d)', active: true },
    { id: 's5', label: 'Custom: AUM tier A/B', active: false },
  ],
  activities: [
    {
      id: 'a1',
      type: 'open',
      message: 'Opened “Portfolio fit” email',
      actorName: 'Jordan Lee',
      actorInitials: 'JL',
      at: new Date(Date.now() - 120e3).toISOString(),
    },
    {
      id: 'a2',
      type: 'click',
      message: 'Clicked CTA — schedule briefing',
      actorName: 'Sam Rivera',
      actorInitials: 'SR',
      at: new Date(Date.now() - 240e3).toISOString(),
    },
    {
      id: 'a3',
      type: 'conversion',
      message: 'Booked meeting (HubSpot)',
      actorName: 'Alex Chen',
      actorInitials: 'AC',
      at: new Date(Date.now() - 400e3).toISOString(),
    },
    {
      id: 'a4',
      type: 'reply',
      message: 'Re: allocation question',
      actorName: 'Morgan Price',
      actorInitials: 'MP',
      at: new Date(Date.now() - 900e3).toISOString(),
    },
    {
      id: 'a5',
      type: 'unsubscribe',
      message: 'Unsubscribed from nurture',
      actorName: 'R. Taylor',
      actorInitials: 'RT',
      at: new Date(Date.now() - 1200e3).toISOString(),
    },
    {
      id: 'a6',
      type: 'failure',
      message: 'Webhook timeout (retry 2/5)',
      actorName: 'system',
      actorInitials: '·',
      at: new Date(Date.now() - 1500e3).toISOString(),
    },
  ],
  alerts: [
    {
      id: 'al1',
      severity: 'warning',
      title: 'Bounce rate elevated',
      detail: 'Segment “Cold import Q1” is at 2.1% — review list hygiene.',
    },
    {
      id: 'al2',
      severity: 'critical',
      title: '12 failed sends (last hour)',
      detail: 'Provider returned 4xx for transactional subdomain.',
    },
    {
      id: 'al3',
      severity: 'info',
      title: 'Webhook backlog clearing',
      detail: 'CRM webhook consumer caught up after 6m delay.',
    },
  ],
  abTests: [
    {
      id: 'ab1',
      campaignName: 'Subject line test — nurture 3',
      variants: [
        { id: 'v1', name: 'Variant A — direct', sent: 14000, conversions: 412, conversionRatePct: 2.94 },
        { id: 'v2', name: 'Variant B — curiosity', sent: 13800, conversions: 489, conversionRatePct: 3.54 },
      ],
      winnerVariantId: 'v2',
      liftPct: 20.4,
      confidencePct: 94,
    },
  ],
  aiRecommendations: [
    {
      id: 'ai1',
      title: 'Shift Tuesday sends +2h',
      detail: 'Open rate 8% higher for accredited segment between 10–11am local.',
      impact: 'high',
    },
    {
      id: 'ai2',
      title: 'Trim email 3 body',
      detail: 'Scroll depth drops 22% after paragraph 4 — consider one CTA only.',
      impact: 'medium',
    },
    {
      id: 'ai3',
      title: 'Expand “event_rsvp” pool',
      detail: '324 high-score leads match behavior similar to top converters.',
      impact: 'medium',
    },
    {
      id: 'ai4',
      title: 'Add SMS only after 2 opens',
      detail: 'Reduce unsubscribe spike on first-touch SMS by 40% in lookalike cohorts.',
      impact: 'low',
    },
  ],
  integrations: [
    { id: 'i1', name: 'SMTP / Transactional', connected: false, detail: 'Configure Resend or SES' },
    { id: 'i2', name: 'Twilio SMS', connected: false, detail: 'Account SID + messaging service' },
    { id: 'i3', name: 'Webhooks', connected: true, detail: '3 endpoints active' },
    { id: 'i4', name: 'Stripe', connected: true, detail: 'Read-only revenue attribution' },
    { id: 'i5', name: 'CRM sync', connected: false, detail: 'HubSpot / Salesforce — OAuth' },
    { id: 'i6', name: 'API keys', connected: true, detail: 'Rotate every 90 days' },
    { id: 'i7', name: 'Domain auth (SPF/DKIM)', connected: false, detail: 'Add DNS records on sending domain' },
  ],
};